import type { InfoCardFieldDataBinding } from '@/types/dataBindings'
import type { SceneObject } from '@/types/scene'
import { dashboardMetricsBucketKey } from '@/dashboard/dashboardMetricsBucketKey'

/** 场景内已出现过的 liveData 点路径（去重）及中文说明 */
export interface SceneDataKeyCatalogEntry {
  dataKey: string
  /** 供 <option> 的 label 属性或展示 */
  hint: string
}

/** 组态 field 下拉用：与 metrics 桶键对应的指标名候选 */
export interface SceneMetricFieldCandidate {
  field: string
  label: string
}

function objectDisplayName(o: SceneObject): string {
  return (o.name && o.name.trim()) || o.id
}

/** 与数据绑定、运行时一致：有 metricKey 用 metricKey，否则用 dataKey 最后一段 */
export function effectiveMetricKeyFromInfoBinding(bind: Pick<InfoCardFieldDataBinding, 'dataKey' | 'metricKey'>): string {
  const mk = bind.metricKey?.trim()
  if (mk) return mk
  const dk = bind.dataKey?.trim()
  if (!dk) return ''
  const parts = dk.split('.').filter(Boolean)
  return parts[parts.length - 1] || ''
}

/**
 * 扫描场景中已保存的 dataBindings，聚合 liveData 路径与按对象桶键分组的指标 field 候选。
 */
export function buildSceneDataKeyCatalog(objects: SceneObject[]): {
  pathEntries: SceneDataKeyCatalogEntry[]
  fieldCandidatesByBucketKey: Record<string, SceneMetricFieldCandidate[]>
} {
  const pathSources = new Map<string, string[]>()

  const addPath = (dataKey: string, sourceLabel: string) => {
    const dk = dataKey.trim()
    if (!dk) return
    const arr = pathSources.get(dk) ?? []
    if (!arr.includes(sourceLabel)) arr.push(sourceLabel)
    pathSources.set(dk, arr)
  }

  const fieldByBucket = new Map<string, Map<string, string>>()

  const addFieldCandidate = (obj: SceneObject, bind: InfoCardFieldDataBinding) => {
    const field = effectiveMetricKeyFromInfoBinding(bind)
    if (!field) return
    const bucket = dashboardMetricsBucketKey(obj)
    const fk = bind.fieldKey?.trim() || field
    let perObj = fieldByBucket.get(bucket)
    if (!perObj) {
      perObj = new Map()
      fieldByBucket.set(bucket, perObj)
    }
    const label = fk === field ? `${field}（信息卡）` : `${field}（信息卡字段 ${fk}）`
    if (!perObj.has(field)) perObj.set(field, label)
  }

  for (const o of objects) {
    if (!o?.id) continue
    const nm = objectDisplayName(o)
    const db = o.dataBindings
    if (!db) continue

    const sl = db.statusLight?.dataKey
    if (sl) addPath(sl, `${nm} · 状态标签`)

    for (const f of db.infoCard?.fields ?? []) {
      if (f.dataKey?.trim()) {
        addPath(f.dataKey, `${nm} · 信息卡 · ${f.fieldKey || '字段'}`)
        addFieldCandidate(o, f)
      }
    }

    const ca = db.colorAlert?.dataKey
    if (ca) addPath(ca, `${nm} · 颜色告警`)
  }

  const pathEntries: SceneDataKeyCatalogEntry[] = [...pathSources.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dataKey, sources]) => ({
      dataKey,
      hint: `${sources.join('；')} — ${dataKey}`,
    }))

  const fieldCandidatesByBucketKey: Record<string, SceneMetricFieldCandidate[]> = {}
  for (const [bucket, m] of fieldByBucket) {
    fieldCandidatesByBucketKey[bucket] = [...m.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([field, label]) => ({ field, label }))
  }

  return { pathEntries, fieldCandidatesByBucketKey }
}

/** 根据组态 sourceKey（assetCode 或对象 id）解析场景对象 */
export function findSceneObjectBySourceKey(
  objects: SceneObject[] | undefined,
  sourceKey: string
): SceneObject | undefined {
  if (!objects?.length || !sourceKey.trim()) return undefined
  return objects.find((o) => o.assetCode === sourceKey || o.id === sourceKey)
}
